import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCircle, ExternalLink, KeyRound, Handshake, FileSignature, Hourglass, XOctagon, AlertTriangle, Banknote, Gavel, FileX, Send, FileWarning, CalendarClock, MailWarning, Phone, ShieldAlert, Sparkles, ArrowLeftRight } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Notification } from '../../types';
import { Link } from 'react-router-dom';
import { abrirSolicitacao, idDaSolicitacaoNoLink } from '../lib/abrirSolicitacao';

/**
 * Para onde vai o clique de um aviso que virou demanda do Painel de Cobrança e
 * Documentos (a rota `/financeiro/esteira` — "esteira" é o nome interno).
 *
 * Estes avisos existem desde antes do painel e apontavam para o lugar onde o
 * documento *nasce* (a UC, a régua), não para onde ele é *trabalhado*. Agora
 * caem na fila certa: o sino volta a ser só aviso, e o painel é onde se resolve.
 *
 * Avisos que não apontam para uma fila única vão pelo grupo — "documento
 * assinado" pode ser notificação de mora ou comunicado de advertência, e a
 * pessoa distingue olhando a lista.
 *
 * Devolve `null` para tudo que não é da esteira, deixando o `link` original valer.
 */
const destinoNaEsteira = (n: Notification): string | null => {
    const esteira = (busca: string) => `/financeiro/esteira?${busca}`;

    switch (n.type) {
        case 'mora_sem_assinatura':
            return esteira('fila=mora_sem_assinatura');
        case 'mora_prazo_5du':
            return esteira('fila=mora_prazo_vencido');
        case 'advertencia_prazo':
            return esteira('fila=advertencia_prazo_vencido');
        case 'cobranca_followup':
            return esteira('fila=followup_vencido');
        case 'notificacao_encaminhada':
            return esteira('fila=notificacao_extrajudicial');

        // Assinado e ainda não enviado, lembrete de assinatura e e-mail recusado
        // pelo servidor do cliente: todos podem ser mora ou advertência.
        // A mora (notificação extrajudicial) tem grupo próprio desde a #6209.
        case 'documento_assinado':
        case 'documento_sem_envio':
        case 'lembrete_assinatura':
        case 'termo_email_nao_entregue':
            return esteira(n.metadata?.doc_tipo === 'mora' ? 'grupo=notificacoes' : 'grupo=documentos');

        case 'cobranca_acao_manual': {
            const porEtapa: Record<string, string> = {
                'D+4': 'regua_d4', 'D+10': 'regua_d10', 'D+17': 'regua_d17', 'D+24': 'regua_d24',
            };
            const fila = porEtapa[String(n.metadata?.etapa ?? '')];
            return fila ? esteira(`fila=${fila}`) : esteira('');
        }

        default:
            return null;
    }
};

const NotificationDropdown = () => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const unreadCount = notifications.filter(n => !n.read).length;

    /**
     * Para onde o clique leva.
     *
     * Recado do vigia da Vick gravado antes de 01/09/2026 tem `link` seco
     * (`/vick`), sem o módulo: o clique abria a fila inteira ordenada por
     * gravidade e o alerta citado no texto ficava enterrado abaixo dos urgentes
     * de outro time — parecia não levar a lugar nenhum. O módulo sempre esteve
     * no `metadata`, então dá para consertar as que já estão na caixa, e não só
     * as próximas.
     */
    const destinoDaNotificacao = (n: Notification): string | null | undefined => {
        if (n.type === 'ia_alerta') {
            // O id vai junto para a /vick abrir ESTE recado — o texto do "Resumo do
            // dia" só existia aqui, cortado em duas linhas.
            const params = new URLSearchParams({ notificacao: String(n.id) });
            if (n.metadata?.modulo) params.set('modulo', String(n.metadata.modulo));
            return `/vick?${params.toString()}`;
        }
        return destinoNaEsteira(n) ?? n.link;
    };

    useEffect(() => {
        if (user) {
            fetchNotifications();

            // Realtime subscription
            const subscription = supabase
                .channel('public:notifications')
                .on('postgres_changes', {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${user.id}`
                }, (payload) => {
                    setNotifications(prev => [payload.new as Notification, ...prev]);
                })
                .subscribe();

            return () => {
                supabase.removeChannel(subscription);
            };
        }
    }, [user]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const fetchNotifications = async () => {
        if (!user) return;
        setLoading(true);
        const { data } = await supabase
            .from('notifications')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(20);

        if (data) {
            setNotifications(data as Notification[]);
        }
        setLoading(false);
    };

    const markAllAsRead = async () => {
        if (!user) return;
        // Optimistic update
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));

        await supabase
            .from('notifications')
            .update({ read: true })
            .eq('user_id', user.id)
            .eq('read', false);
    };

    const markAsRead = async (id: string) => {
        // Optimistic update
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));

        await supabase
            .from('notifications')
            .update({ read: true })
            .eq('id', id);
    };

    const filteredNotifications = activeTab === 'all'
        ? notifications
        : notifications.filter(n => !n.read);

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative text-gray-400 hover:text-primary transition-colors p-2 rounded-full hover:bg-gray-100"
            >
                <Bell size={20} />
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white flex items-center justify-center text-[10px] text-white font-bold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 top-12 w-80 md:w-96 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 z-50 animate-fade-in overflow-hidden">
                    <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/50">
                        <h3 className="font-bold text-gray-900 dark:text-white">Notificações</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="text-xs text-primary font-bold hover:text-primary/80"
                            >
                                Marcar todas como lidas
                            </button>
                        )}
                    </div>

                    <div className="flex border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                        <button
                            onClick={() => setActiveTab('all')}
                            className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 ${activeTab === 'all' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                        >
                            Todas
                        </button>
                        <button
                            onClick={() => setActiveTab('unread')}
                            className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 ${activeTab === 'unread' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                        >
                            Não lidas
                        </button>
                    </div>

                    <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                        {loading && notifications.length === 0 ? (
                            <div className="p-8"><LoadingSpinner label="Carregando..." size="sm" /></div>
                        ) : filteredNotifications.length === 0 ? (
                            <div className="p-12 text-center text-gray-500 dark:text-gray-400">
                                <span className="text-4xl block mb-2">😉</span>
                                <p className="text-sm font-medium">Não há nada novo por aqui</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-50 dark:divide-gray-700">
                                {filteredNotifications.map(notification => (
                                    <div
                                        key={notification.id}
                                        onClick={() => markAsRead(notification.id)}
                                        className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer group relative ${!notification.read ? 'bg-blue-50/30 dark:bg-blue-900/10' : 'bg-white dark:bg-gray-800'}`}
                                    >
                                        <div className="flex gap-3">
                                            <div className="mt-1 shrink-0">
                                                {notification.type === 'task_assigned' && <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center"><Check size={14} /></div>}
                                                {notification.type === 'task_completed' && <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center"><CheckCircle size={14} /></div>}
                                                {notification.type === 'negotiation_assigned' && <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center"><ExternalLink size={14} /></div>}
                                                {notification.type === 'mention' && <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center">@</div>}
                                                 {notification.type === 'analysis_completed' && <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center"><Check size={14} /></div>}
                                                 {notification.type === 'password_reset_request' && <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center"><KeyRound size={14} /></div>}
                                                 {notification.type === 'system' && <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400 flex items-center justify-center"><Bell size={14} /></div>}
                                                 {notification.type === 'parceiro_cadastro' && <div className="w-8 h-8 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 flex items-center justify-center"><Handshake size={14} /></div>}
                                                 {notification.type === 'contract_signed' && <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 flex items-center justify-center"><FileSignature size={14} /></div>}
                                                 {notification.type === 'contract_pending' && <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center"><Hourglass size={14} /></div>}
                                                 {notification.type === 'termo_reprovado' && <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center"><XOctagon size={14} /></div>}
                                                 {notification.type === 'cobranca_d15' && <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center"><AlertTriangle size={14} /></div>}
                                                 {notification.type === 'repasse_equatorial_falhou' && <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center"><Banknote size={14} /></div>}
                                                 {/* Dinheiro entrou no Sicoob e nenhuma fatura tem o boleto — a baixa não foi feita */}
                                                 {notification.type === 'sicoob_pagamento_sem_fatura' && <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center"><Banknote size={14} /></div>}
                                                 {notification.type === 'troca_dono_uc' && <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center"><ArrowLeftRight size={14} /></div>}
                                                 {notification.type === 'mora_sem_assinatura' && (
                                                     /* Âmbar cobra a assinatura; vermelho é documento morto (expirado ou nunca gerado) */
                                                     notification.metadata?.estagio === 'pendente'
                                                         ? <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center"><FileSignature size={14} /></div>
                                                         : <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center"><FileX size={14} /></div>
                                                 )}
                                                 {notification.type === 'mora_prazo_5du' && (
                                                     /* Verde quando o associado quitou/parcelou; vermelho quando o prazo venceu sem pagamento */
                                                     notification.metadata?.resultado === 'pago' || notification.metadata?.resultado === 'parcelado'
                                                         ? <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center"><CheckCircle size={14} /></div>
                                                         : <div className="w-8 h-8 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 flex items-center justify-center"><Gavel size={14} /></div>
                                                 )}
                                                 {/* Servidor de e-mail do cliente recusou o convite do Autentique — o termo nunca chegou */}
                                                 {notification.type === 'termo_email_nao_entregue' && <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center"><MailWarning size={14} /></div>}
                                                 {/* Documento assinado pelo representante — emissor deve baixar o PDF e enviar manualmente */}
                                                 {notification.type === 'documento_assinado' && <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 flex items-center justify-center"><FileSignature size={14} /></div>}
                                                 {/* Assinado mas o envio ao cliente ainda não foi registrado */}
                                                 {notification.type === 'documento_sem_envio' && <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center"><Send size={14} /></div>}
                                                 {/* Prazo de 15 dias da advertência encerrado */}
                                                 {notification.type === 'advertencia_prazo' && <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center"><FileWarning size={14} /></div>}
                                                 {/* Follow-up de negociação de cobrança agendado, ou cliente transferido para o analista */}
                                                 {notification.type === 'cobranca_followup' && <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center"><CalendarClock size={14} /></div>}
                                                 {/* Cliente encaminhado para a notificação extrajudicial pelo "Gerenciar Cobrança" */}
                                                 {notification.type === 'notificacao_encaminhada' && <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center"><FileWarning size={14} /></div>}
                                                 {/* Lembrete manual pedindo a assinatura do procurador (botão "Enviar lembrete" no Histórico de Documentos) */}
                                                 {notification.type === 'lembrete_assinatura' && <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center"><FileSignature size={14} /></div>}
                                                 {/* Etapa de contato humano da régua de cobrança — a cor sobe junto com a gravidade */}
                                                 {notification.type === 'cobranca_acao_manual' && (
                                                     notification.metadata?.etapa === 'D+24'
                                                         ? <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center"><ShieldAlert size={14} /></div>
                                                         : notification.metadata?.etapa === 'D+17'
                                                             ? <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center"><FileWarning size={14} /></div>
                                                             : <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center"><Phone size={14} /></div>
                                                 )}
                                                 {/* Prazo (vencimento) de uma Solicitação de TI/Marketing vencendo hoje ou amanhã */}
                                                 {notification.type === 'solicitacao_prazo_proximo' && <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center"><CalendarClock size={14} /></div>}
                                                 {/* Recado do vigia da Vick — pode reunir vários alertas num aviso só.
                                                     A cor sobe com a pior severidade do lote. */}
                                                 {notification.type === 'ia_alerta' && (
                                                     notification.metadata?.severidades?.includes('alta')
                                                         ? <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center"><Sparkles size={14} /></div>
                                                         : <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Sparkles size={14} /></div>
                                                 )}
                                                 {/* Usina que parou de reportar ou de gerar. Não confundir com 'geradora_alarme',
                                                     que é evento do inversor (tensão, frequência) e tem outro público. */}
                                                 {notification.type === 'geradora_alerta' && (
                                                     notification.metadata?.falha === 'normalizou'
                                                         ? <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center"><CheckCircle size={14} /></div>
                                                         /* Vermelho: a usina em si parou. Âmbar: só o coletor caiu, é problema de TI */
                                                         : notification.metadata?.falha === 'sem_geracao'
                                                             ? <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center"><ShieldAlert size={14} /></div>
                                                             : <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center"><AlertTriangle size={14} /></div>
                                                 )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm ${!notification.read ? 'font-bold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                                                    {notification.title}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{notification.content}</p>
                                                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">
                                                    {new Date(notification.created_at).toLocaleDateString()} às {new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                            </div>
                                            {!notification.read && (
                                                <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0"></div>
                                            )}
                                        </div>
                                        {notification.type === 'termo_reprovado' ? (
                                            /* Reabre o pop-up de reprovação (TermoReprovadoPopup) com os detalhes */
                                            <button
                                                className="absolute inset-0 z-10"
                                                onClick={() => {
                                                    markAsRead(notification.id);
                                                    setIsOpen(false);
                                                    window.dispatchEvent(new CustomEvent('vigorhub:open-termo-reprovado', { detail: notification }));
                                                }}
                                            />
                                        ) : notification.type === 'cobranca_d15' ? (
                                            /* Abre a Central de Inadimplência direto na aba "Vencidas há 15 dias" */
                                            <button
                                                className="absolute inset-0 z-10"
                                                onClick={() => {
                                                    markAsRead(notification.id);
                                                    setIsOpen(false);
                                                    window.dispatchEvent(new CustomEvent('vigorhub:open-inadimplencia', { detail: { tab: 'vencidas15' } }));
                                                }}
                                            />
                                        ) : idDaSolicitacaoNoLink(destinoDaNotificacao(notification)) ? (
                                            /* Solicitação abre pelo modal global (sem navegar) — ver
                                               src/lib/abrirSolicitacao.ts. A detecção é pelo FORMATO do link
                                               (idDaSolicitacaoNoLink), não pelo `type` da notificação — cobre de
                                               graça as já gravadas no banco antes desta mudança, inclusive as
                                               que os triggers SQL escrevem, sem precisar de migration nenhuma. */
                                            <button
                                                type="button"
                                                className="absolute inset-0 z-10 text-left"
                                                onClick={() => {
                                                    const id = idDaSolicitacaoNoLink(destinoDaNotificacao(notification));
                                                    if (id) abrirSolicitacao(id);
                                                    markAsRead(notification.id);
                                                    setIsOpen(false);
                                                }}
                                            />
                                        ) : destinoDaNotificacao(notification) ? (
                                            <Link
                                                to={destinoDaNotificacao(notification) as string}
                                                className="absolute inset-0 z-10"
                                                onClick={() => { markAsRead(notification.id); setIsOpen(false); }}
                                            />
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationDropdown;
